import { addLicense } from "@src/app/licenseManager/api";
import { deprecatedDoNotUseLogError } from "@src/appV2/lib/analytics";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { Worker } from "@src/appV2/Worker/api/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import moment from "moment-timezone";

import {
  LICENSE_STATUSES_VALUE_MAPPING,
  SUMMARY_TEXT_LICENSE_STATUSES_ORDER,
  logType,
} from "./constants";
import { ICreateLicense, ILicenseDetails, LicenseDefaultActiveRule } from "./interfaces";
import { updatePreference } from "../openShifts/api";

export const getLicensesCountByStatus = (
  licenses: ILicenseDetails[]
): { [key in LicenseStatuses]?: number } => {
  const licensesCountByStatus = {};
  licenses.forEach((license) => {
    const daysUntilExpired = moment(license?.expiresAt).diff(moment(), "days", true);
    let { status } = license;
    if (daysUntilExpired <= 0) {
      status = LicenseStatuses.EXPIRED;
    } else if (daysUntilExpired <= 90 && license.status === LicenseStatuses.ACTIVE) {
      // Expiring in 3 months
      status = LicenseStatuses.EXPIRING;
    }

    if (!status) {
      return;
    }

    if (status && !(status in licensesCountByStatus)) {
      licensesCountByStatus[status] = 0;
    }
    licensesCountByStatus[status] += 1;
  });
  return licensesCountByStatus;
};

export const getLicensesSummaryTextStrings = (licensesCountByStatus: {
  [key in LicenseStatuses]?: number;
}): string[] => {
  const summaryText: string[] = [];
  for (const licenseStatus of SUMMARY_TEXT_LICENSE_STATUSES_ORDER) {
    if (licensesCountByStatus[LicenseStatuses[licenseStatus]]) {
      if (
        LicenseStatuses[licenseStatus] === LicenseStatuses.EXPIRING &&
        licensesCountByStatus[LicenseStatuses.ACTIVE]
      ) {
        // Ignoring EXPIRING one as we are adding it with ACTIVE status
        continue;
      }
      if (LicenseStatuses[licenseStatus] === LicenseStatuses.ARCHIVED) {
        // Ignoring ARCHIVED
        continue;
      }

      if (
        LicenseStatuses[licenseStatus] === LicenseStatuses.ACTIVE &&
        licensesCountByStatus[LicenseStatuses.EXPIRING]
      ) {
        // Logic to include active & expiring summary text
        const activeCount =
          (licensesCountByStatus[LicenseStatuses.ACTIVE] || 0) +
          (licensesCountByStatus[LicenseStatuses.EXPIRING] || 0);
        const expiringCount = licensesCountByStatus[LicenseStatuses.EXPIRING];
        summaryText.push(
          `${activeCount} ${
            LICENSE_STATUSES_VALUE_MAPPING[LicenseStatuses.ACTIVE]
          } (${expiringCount} ${LICENSE_STATUSES_VALUE_MAPPING[LicenseStatuses.EXPIRING]})`
        );
      } else {
        summaryText.push(
          `${licensesCountByStatus[LicenseStatuses[licenseStatus]]} ${
            LICENSE_STATUSES_VALUE_MAPPING[LicenseStatuses[licenseStatus]]
          }`
        );
      }
    }
  }
  return summaryText;
};

export const getLicensesSummaryText = (licensesCountByStatus: {
  [key in LicenseStatuses]?: number;
}): JSX.Element[] => {
  const summaryTextStrings = getLicensesSummaryTextStrings(licensesCountByStatus);
  return summaryTextStrings.map((text, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Text key={index} variant="body1">
      {text}
    </Text>
  ));
};

export const updateLicenseAndQualificationPreference = async (
  qualification: string,
  userId: string
): Promise<Worker | undefined> => {
  const LOG_CONTEXT = "updateLicenseAndQualificationPreference";

  const license = {
    qualification,
    state: "Any",
    multiState: false,
    number: "",
    logType: logType.IMPORT,
  };
  try {
    await addLicense(license, userId, true);
    return await updatePreference("qualification", qualification, true);
  } catch (error) {
    deprecatedDoNotUseLogError({
      message: `${LOG_CONTEXT} Error while updating license and qualification preference userId: ${userId}, qualification:- ${qualification}. Error:- ${error?.message}`,
    });
    throw error;
  }
};

interface IsLicenseDefaultActiveParams {
  defaultActiveLicenseRules: LicenseDefaultActiveRule[];
  license: Pick<ICreateLicense, "qualification" | "state">;
}
export function isLicenseDefaultActive(params: IsLicenseDefaultActiveParams): boolean {
  const { license, defaultActiveLicenseRules } = params;
  const checkRulePredicate = (rule: LicenseDefaultActiveRule) =>
    Object.entries(rule).every((ruleEntry) =>
      ruleEntry[1].some((value) => value === license[ruleEntry[0]])
    );
  return defaultActiveLicenseRules.some(checkRulePredicate);
}
